import React, { useMemo } from "react";
import { Table, Typography } from "antd";
import { startOfMonth, endOfMonth } from "date-fns";

const { Title } = Typography;

interface SalaryTypes {
  dailyWages: number;
  fixedSalaries: number;
  hourlyRates: number;
  percentageRates: number;
  temporaryHelps: number;
  travelExpenses: number;
}

interface SalaryEntry {
  _id: string;
  date: string;
  salaryTypes: SalaryTypes;
}

interface Shift {
  _id: string;
  date: string;
  startTime: string;
  endTime: string;
  durationInHours: number;
  shiftType: "DAILY_WAGE" | "TEMPORARY_HELP" | "HOURS" | "LEAVES";
  leavePayPercentage?: number;
  leavePayType?: "DAILY_WAGE" | "HOURS";
  leaveType?: any;
  locationId?: { _id: string; name: string } | string | null;
}

interface ShiftsForUser {
  dailyWage: Shift[];
  temporaryHelp: Shift[];
  hours: Shift[];
  leaves?: Shift[];
}
interface SalaryTableProps {
  salary: SalaryEntry[];
  shiftsForUser: ShiftsForUser;
  selectedMonth: string;
  onTotalChange?: (totalAmount: any) => void;
  isDetailyWageTableVisible: boolean;
  locations?: { _id: string; name: string }[];
}

interface TableRow {
  key: string;
  period: string;
  rate: number;
  hours: string | number; // ostaje fleksibilno
  amount: number;
  percentage?: string;
  leaveType?: string;
  locationName: string;
}

const SalaryTable: React.FC<SalaryTableProps> = ({
  salary,
  shiftsForUser,
  selectedMonth,
  onTotalChange,
  isDetailyWageTableVisible,
  locations,
}) => {
  const monthStart = useMemo(
    () => startOfMonth(new Date(selectedMonth)),
    [selectedMonth]
  );
  const monthEnd = useMemo(
    () => endOfMonth(new Date(selectedMonth)),
    [selectedMonth]
  );

  // Sort salary by date
  const sortedSalary = useMemo(
    () =>
      salary
        ?.filter((s) => s && s.date)
        .sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        ) || [],
    [salary]
  );

  const getLocationName = (locationId: Shift["locationId"]): string => {
    if (!locationId) return "";
    if (typeof locationId === "object") return locationId.name ?? "";
    // locationId je string ID — traži naziv iz liste lokacija
    return locations?.find((l) => l._id === locationId)?.name ?? "";
  };

  const getSalaryForDate = (date: string, preferNonZeroKey?: keyof SalaryTypes): SalaryTypes | null => {
    const target = new Date(date);
    const targetY = target.getUTCFullYear();
    const targetM = target.getUTCMonth();
    const targetD = target.getUTCDate();

    const valid = sortedSalary.filter((s) => {
      const sd = new Date(s.date);
      const y = sd.getUTCFullYear();
      const m = sd.getUTCMonth();
      const d = sd.getUTCDate();
      return (
        y < targetY ||
        (y === targetY && m < targetM) ||
        (y === targetY && m === targetM && d <= targetD)
      );
    });

    if (!valid.length) return null;

    const last = valid[valid.length - 1];

    // Ako zadnji unos ima 0 za traženi tip, pokušaj naći stariji unos s nenultom vrijednošću.
    // Ovo rješava slučaj kad zaposleni ima dnevnicu na jednoj lokaciji (npr. Panorama),
    // ali novije salary entryje na drugim lokacijama s 0 dnevnicom koji bi je overridovali.
    if (preferNonZeroKey && (last.salaryTypes?.[preferNonZeroKey] ?? 0) === 0) {
      const fallback = valid.filter(s => (s.salaryTypes?.[preferNonZeroKey] ?? 0) > 0).pop();
      if (fallback) {
        return { ...last.salaryTypes, [preferNonZeroKey]: fallback.salaryTypes![preferNonZeroKey] };
      }
    }

    return last.salaryTypes;
  };
  const makeTableData = (
    shifts: Shift[],
    key: keyof SalaryTypes
  ): TableRow[] => {
    return shifts
      .filter(
        (shift) =>
          new Date(shift.date) >= monthStart && new Date(shift.date) <= monthEnd
      )
      .map((shift) => {
        const salaryTypes = getSalaryForDate(shift.date, key);
        if (!salaryTypes) return null;

        const rate = salaryTypes[key];
        const amount =
          key === "dailyWages" || key === "temporaryHelps"
            ? rate
            : rate * shift.durationInHours;

        return {
          key: shift._id,
          period: new Date(shift.date).toLocaleDateString(),
          rate,
          hours:
            key === "hourlyRates"
              ? shift.durationInHours.toFixed(2)
              : key === "temporaryHelps"
              ? shift.durationInHours
              : "-",
          amount,
          locationName: getLocationName(shift.locationId),
        };
      })
      .filter((row): row is TableRow => row !== null);
  };

  const dailyWageData = useMemo(
    () => makeTableData(shiftsForUser.dailyWage, "dailyWages"),
    [shiftsForUser, sortedSalary]
  );

  const tempHelpData = useMemo(
    () => makeTableData(shiftsForUser.temporaryHelp, "temporaryHelps"),
    [shiftsForUser, sortedSalary]
  );

  const hourlyData = useMemo(
    () => makeTableData(shiftsForUser.hours, "hourlyRates"),
    [shiftsForUser, sortedSalary]
  );

  const leavesData = useMemo(() => {
    if (!shiftsForUser.leaves) return [];

    return shiftsForUser.leaves
      .filter(
        (shift) =>
          new Date(shift.date) >= monthStart && new Date(shift.date) <= monthEnd
      )
      .map((shift) => {
        const leaveKey: keyof SalaryTypes = shift.leavePayType === "DAILY_WAGE" ? "dailyWages" : "hourlyRates";
        const salaryTypes = getSalaryForDate(shift.date, leaveKey);
        if (!salaryTypes) return null;

        const rate =
          shift.leavePayType === "DAILY_WAGE"
            ? salaryTypes.dailyWages
            : salaryTypes.hourlyRates;

        const baseAmount =
          shift.leavePayType === "DAILY_WAGE"
            ? rate
            : rate * shift.durationInHours;

        const amount = (baseAmount * (shift.leavePayPercentage || 0)) / 100;

        return {
          key: shift._id,
          period: new Date(shift.date).toLocaleDateString(),
          rate,
          hours:
            shift.leavePayType === "HOURS"
              ? shift.durationInHours.toFixed(2)
              : "-",
          amount,
          percentage: `${shift.leavePayPercentage || 0}%`,
          leaveType: shift.leaveType || "",
          locationName: getLocationName(shift.locationId),
        };
      })
      .filter((row) => row !== null) as TableRow[];
  }, [shiftsForUser, sortedSalary]);

  const columns = [
    { title: "Datum", dataIndex: "period", key: "period" },
    { title: "Lokacija", dataIndex: "locationName", key: "locationName", render: (v: string) => v || "-" },
    { title: "Tarifa", dataIndex: "rate", key: "rate" },
    { title: "Sati", dataIndex: "hours", key: "hours" },
    { title: "Procenat", dataIndex: "percentage", key: "percentage" },
    { title: "Vrsta odsustva", dataIndex: "leaveType", key: "leaveType" },
    {
      title: "Iznos",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => (amount || 0).toFixed(2),
    },
  ];

  const totalDaily = dailyWageData.reduce((s, i) => s + i.amount, 0);
  const totalTemp = tempHelpData.reduce((s, i) => s + i.amount, 0);
  const totalHourly = hourlyData.reduce((s, i) => s + i.amount, 0);
  const totalLeaves = leavesData.reduce((s, i) => s + i.amount, 0);
  const grandTotal = totalDaily + totalTemp + totalHourly + totalLeaves;

  React.useEffect(() => {
    onTotalChange?.({ total: grandTotal, leaves: totalLeaves });
  }, [grandTotal, totalLeaves]);

  if (!isDetailyWageTableVisible) return null;

  return (
    <div>
      <Title level={5}>Dnevnice</Title>
      <Table<TableRow>
        columns={columns}
        dataSource={dailyWageData}
        pagination={false}
        rowKey="key"
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={3}>
              <strong>Ukupno</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3}>
              <strong>{totalDaily.toFixed(2)}</strong>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />

      <Title level={5} style={{ marginTop: 20 }}>
        Ispomoć
      </Title>
      <Table<TableRow>
        columns={columns}
        dataSource={tempHelpData}
        pagination={false}
        rowKey="key"
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={3}>
              <strong>Ukupno</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3}>
              <strong>{totalTemp.toFixed(2)}</strong>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />

      <Title level={5} style={{ marginTop: 20 }}>
        Satnica
      </Title>
      <Table<TableRow>
        columns={columns}
        dataSource={hourlyData}
        pagination={false}
        rowKey="key"
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={3}>
              <strong>Ukupno</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3}>
              <strong>{totalHourly.toFixed(2)}</strong>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
      {shiftsForUser.leaves && shiftsForUser.leaves.length > 0 && (
        <>
          <Title level={5} style={{ marginTop: 20 }}>
            Odsustva
          </Title>
          <Table<TableRow>
            columns={columns}
            dataSource={leavesData}
            pagination={false}
            rowKey="key"
            summary={() => {
              const totalLeaves = leavesData.reduce((s, i) => s + i.amount, 0);
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={5}>
                    <strong>Ukupno</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5}>
                    <strong>{totalLeaves.toFixed(2)}</strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </>
      )}

      {/* <div style={{ marginTop: 20, fontWeight: "bold" }}>
        Ukupna zarada: {grandTotal.toFixed(2)} RSD
      </div> */}
    </div>
  );
};

export default SalaryTable;
